import React__default, { useState } from 'react';
import { DropZone } from 'react-aria-components';
import { Spinner } from './Spinner.js';
import { Text } from './Text.js';
import { formatFileType, validateFile } from '../utils/fileUploadHelpers.js';
import './Dropzone.scss';

function composeDefaultDropzoneDescription(accepted) {
    if (!accepted || accepted.length === 0) {
        return 'Drop files here';
    }
    const parts = accepted.map((rule) => {
        const label = formatFileType(rule.type);
        return rule.recommendations ? `${label} (${rule.recommendations})` : label;
    });
    if (parts.length === 1) {
        return `Best results: ${parts[0]}`;
    }
    if (parts.length === 2) {
        return `Best results: ${parts[0]} or ${parts[1]}`;
    }
    const last = parts.pop();
    return `Best results: ${parts.join(', ')}, or ${last}`;
}
function Dropzone({ isDisabled, isLoading: propIsLoading, accepted = [], title = 'Drag and drop images and video here', rounding = '16', titleProps, description = composeDefaultDropzoneDescription(accepted), descriptionProps, onFilesAccepted, onFilesRejected, maxFiles, }) {
    const [internalLoading, setInternalLoading] = useState(false);
    const inputRef = React__default.useRef(null);
    const handleFileUpload = (files) => {
        if (!files || files.length === 0)
            return;
        const acceptedFiles = [];
        const rejectedFiles = [];
        files.forEach((file) => {
            const errors = validateFile(file, accepted);
            if (errors.length === 0) {
                acceptedFiles.push(file);
            }
            else {
                rejectedFiles.push({ file, errors });
            }
        });
        if (maxFiles && acceptedFiles.length > maxFiles) {
            const overflow = acceptedFiles.splice(maxFiles);
            overflow.forEach((file) => {
                rejectedFiles.push({
                    file,
                    errors: ['Too many files'],
                });
            });
        }
        if (acceptedFiles.length > 0) {
            onFilesAccepted?.(acceptedFiles);
        }
        if (rejectedFiles.length > 0) {
            onFilesRejected?.(rejectedFiles);
        }
        setInternalLoading(false);
    };
    const isLoading = internalLoading || propIsLoading;
    const mimeTypes = accepted.map((rule) => rule.type);
    const titleIsString = typeof title === 'string';
    const descriptionIsString = typeof description === 'string';
    return (React__default.createElement(DropZone, { "data-rev-component": 'dropzone', isDisabled: isDisabled, "data-rev-loading": isLoading, "data-rev-rounding": rounding, getDropOperation: (types) => (mimeTypes.some((t) => types.has(t)) ? 'copy' : 'cancel'), onDrop: async (e) => {
            setInternalLoading(true);
            const files = await Promise.all(e.items.filter((item) => item.kind === 'file').map((item) => item.getFile()));
            handleFileUpload(files);
        } },
        React__default.createElement("div", { className: 'dropzone-container--rev', onClick: () => {
                if (isDisabled)
                    return;
                inputRef.current?.click();
            } }, isLoading ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement("div", { className: 'dropzone-loading-overlay-group--rev' },
                React__default.createElement(Spinner, { size: 'small' })),
            React__default.createElement(Text, { className: 'dropzone__loading-text--rev', size: 'bodySmall', weight: 'regular', align: 'center' }, "Add media"))) : (React__default.createElement(React__default.Fragment, null,
            titleIsString ? (React__default.createElement(Text, { size: 'bodySmall', weight: 'semibold', className: 'dropzone__title--rev', ...(titleProps || {}) }, title)) : (React__default.createElement(React__default.Fragment, null, title)),
            descriptionIsString ? (React__default.createElement(Text, { size: 'footnote', weight: 'regular', className: 'dropzone__description--rev', ...(descriptionProps || {}) }, description)) : (React__default.createElement(React__default.Fragment, null, description)))))));
}

export { Dropzone };
//# sourceMappingURL=Dropzone.js.map
